// istanbul ignore file
import Highcharts from 'highcharts'
import HighchartsNoData from 'highcharts/modules/no-data-to-display'
import { type Option } from '../../../components/GenXDropdown'
import moment from 'moment-timezone'

// Initialize the no-data module
HighchartsNoData(Highcharts)

// Custom interface for data points
interface CustomPoint extends Highcharts.PointOptionsObject {
  units: string
  registerName: string
  readingTime: string
}

export interface IConfigObjectItem {
  serialNumber: string
  checked: boolean
  color: string
  selectedSignalData: null | Option
}

/**
 * Formatter for x-axis labels in Highcharts.
 * Displays the date and time for the first tick and when the day changes; otherwise, shows only the time.
 * @this Highcharts.AxisLabelsFormatterContextObject - The context provided by Highcharts for label formatting.
 * @returns {string} HTML string for the axis label. Requires `labels.useHTML = true` in Highcharts configuration.
 **/

export const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject): string {
  const curr = moment(this.value).utc()
  const tickPositions = Array.isArray(this.axis.tickPositions) ? this.axis.tickPositions : []
  const pos = tickPositions.indexOf(Number(this.value))

  const formatDateTime = (m: moment.Moment): string => {
    return `<div style="text-align:center;font-size:12px;"><b>${m.format('D MMM')}</b><br>${m.format('hh:mm')} ${m.format('A')}</div>`
  }

  const formatTimeOnly = (m: moment.Moment): string => {
    return `<div style="text-align:center;font-size:12px;">${m.format('hh:mm')} ${m.format('A')}</div>`
  }

  // Show date and time if it's the first tick or not found
  if (pos <= 0) {
    return formatDateTime(curr)
  }

  const prev = moment(tickPositions[pos - 1]).utc()

  // Show date and time if the day has changed
  if (!curr.isSame(prev, 'day')) {
    return formatDateTime(curr)
  }

  // Otherwise, show only time
  return formatTimeOnly(curr)
}

export const tooltipPositioner: Highcharts.TooltipPositionerCallbackFunction = function (labelWidth, labelHeight, point) {
  const chart = this.chart
  const plotLeft = chart.plotLeft
  const plotWidth = chart.plotWidth
  const pointX = point.plotX + plotLeft
  let x
  if (pointX > plotLeft + plotWidth / 2) {
    x = pointX - labelWidth - 20
  } else {
    x = pointX + 20
  }
  x = Math.max(plotLeft, Math.min(x, plotLeft + plotWidth - labelWidth))
  const y = Math.max(chart.plotTop, point.plotY + chart.plotTop - labelHeight - 10)
  return { x, y }
}

/**
 * Formatter for Highcharts tooltip points.
 * @this Highcharts.Point - The point object provided by Highcharts, which may include additional properties such as `units`.
 * @returns {string} HTML string for the tooltip content. Requires `tooltip.useHTML = true` in Highcharts options.
 **/

// Add this helper function
function escapeHtml (str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// Update your formatter:
export function customTooltipPointFormatter (this: Highcharts.Point): string {
  const color = typeof this.color === 'string' ? this.color : '#000'
  const seriesName = escapeHtml(this.series?.name ?? '')
  const yValue = this.y !== undefined ? this.y : ''
  const units = typeof ((this as unknown) as CustomPoint).units !== 'undefined'
    ? ` ${escapeHtml(((this as unknown) as CustomPoint).units)}`
    : ''
  return `<span style="color:${color}">\u25CF</span> ${seriesName}: <b>${yValue}${units}</b><br/>`
}

export type IConfigObject = Record<number, IConfigObjectItem>

export const getChartOptions = (mergedKpiParamData: any[], configObject: IConfigObject): Highcharts.Options => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  // Rearrange mergedKpiParamData based on configObject
  const rearrangedData = Object.values(configObject)
    .filter(config => config.checked)
    .map(config => {
      const dataItem = mergedKpiParamData.find(item => item.parameterId === config.selectedSignalData?.value)
      return dataItem !== null && dataItem !== undefined ? { ...dataItem, color: config.color } : null
    })
    .filter(item => item !== null)

  const getSeriesObject = (item: any, entry: any): any => {
    const readingTime = moment.utc(entry.readingTime).tz(userTimezone)
    return {
      x: Date.UTC(readingTime.year(), readingTime.month(), readingTime.date(), readingTime.hours(), readingTime.minutes(), readingTime.seconds()),
      y: entry.value,
      units: item.units,
      registerName: item.parameterName
    }
  }

  // Assign each series to its own yAxis for autoscaling
  const series: Highcharts.SeriesLineOptions[] = rearrangedData.map((item, idx) => {
    const seriesData: CustomPoint[] = item.assetIntervalReadingData.map((entry: any) => {
      return getSeriesObject(item, entry)
    })
    return {
      type: 'line',
      name: item.parameterName,
      data: seriesData,
      dataGrouping: {
        enabled: true,
        approximation: 'average',
        groupPixelWidth: 10,
        units: [['hour', [1]], ['day', [1]], ['week', [1]], ['month', [1]]]
      },
      color: item.color,
      fillOpacity: 0,
      lineWidth: 2,
      marker: {
        enabled: true,
        radius: 0,
        symbol: 'circle'
      },
      turboThreshold: 500000,
      compare: 'percent',
      showInNavigator: true,
      yAxis: idx // Assign each series to its own yAxis
    }
  })

  // Create a yAxis for each series for autoscaling
  const yAxes: Highcharts.YAxisOptions[] = rearrangedData.length === 0
    ? [{ visible: false }]
    : rearrangedData.map((item, idx) => {
      const hasData = Array.isArray(item.assetIntervalReadingData) && item.assetIntervalReadingData.length > 0
      return {
        title: {
          text: item.parameterName,
          style: {
            visibility: 'hidden'
          }
        },
        labels: {
          style: {
            color: item.color,
            fontSize: '14px'
          }
        },
        opposite: idx % 2 === 1, // Alternate sides for yAxes
        min: undefined,
        max: undefined,
        showLastLabel: true,
        gridLineWidth: 0,
        lineWidth: hasData ? 2 : 0,
        lineColor: item.color,
        visible: hasData
      }
    })

  return {
    chart: {
      height: 380,
      zooming: {
        type: 'xy'
      },
      type: 'stock',
      panning: {
        enabled: true,
        type: 'x'
      },
      panKey: 'shift',
      style: {
        fontSize: '16px',
        fontFamily: 'Honeywell Sans Web'
      },
      events: {
        load: function (this: Highcharts.Chart) {
          this.showLoading('Loading data...')
          setTimeout(() => {
            this.hideLoading()
          }, 2000)
        },
        redraw: function (this: Highcharts.Chart) {
          this.hideLoading()
        },
        render: function (this: Highcharts.Chart) {
          // Remove previous legendItemClick handlers to avoid duplicates
          this.series.forEach((series, idx: number) => {
            Highcharts.removeEvent(series, 'legendItemClick')
            Highcharts.addEvent(series, 'legendItemClick', function (event: any) {
              const chart = this.chart
              const yAxis = chart.yAxis[idx]
              if (yAxis !== undefined && yAxis !== null) {
                // Toggle visibility based on what it will be after click
                const willBeVisible = !this.visible
                yAxis.update({ visible: willBeVisible }, false)
                chart.redraw()
              }
            })
          })

          // Keep your resetZoomButton alignment logic if needed
          const chart = this as Highcharts.Chart & { resetZoomButton?: any }
          const button = chart.resetZoomButton
          if (button?.element !== undefined) {
            button.align({
              align: 'left',
              x: 120,
              y: 10
            }, false, 'plotBox')
          }
        }
      }
    },
    credits: {
      enabled: false
    },
    exporting: {
      enabled: true,
      buttons: {
        contextButton: {
          menuItems: ['viewFullscreen']
        }
      }
    },
    title: {
      text: ''
    },
    xAxis: {
      crosshair: true,
      title: {
        text: ''
      },
      type: 'datetime',
      labels: {
        formatter: xAxisLabelFormatter,
        useHTML: true,
        step: 2,
        allowOverlap: false
      },
      minRange: 60 * 1000
    },
    rangeSelector: {
      selected: 4
    },
    yAxis: yAxes, // Use multiple yAxes for autoscaling
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      itemMarginTop: 10,
      itemMarginBottom: 10,
      itemStyle: {
        fontWeight: '400',
        fontSize: '16px',
        fontFamily: '"Honeywell Sans Web", Arial, Helvetica, sans-serif'
      },
      backgroundColor: 'transparent',
      useHTML: true,
      labelFormatter: function () {
        const name = this.name
        const color = this.color
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `<div style="position: relative;"><span class='icon-class' style="background-color: ${this.visible ? color : color}; display: ${(this.name.length > 0) ? 'inline-table' : 'none'}"></span><span class='icon-class-title' style="color: '#f0f0f0'">${name}</span></div>`
      }
    },
    tooltip: {
      shared: true,
      split: true,
      valueDecimals: 0,
      xDateFormat: '%b %e, %Y %I:%M:%S %p',
      pointFormatter: customTooltipPointFormatter,
      positioner: tooltipPositioner
    },
    plotOptions: {
      series: {
        animation: series.length > 1000 ? false : { duration: 2000 }
      }
    },
    boost: {
      useGPUTranslations: true,
      usePreallocated: true
    },
    series,
    lang: {
      noData: 'NO DATA AVAILABLE'
    },
    noData: {
      style: {
        fontWeight: 'bold',
        fontSize: '15px',
        color: 'white',
        background: 'black'
      }
    },
    loading: {
      labelStyle: {
        color: 'white',
        fontWeight: 'bold',
        fontSize: '20px',
        background: 'black',
        position: 'relative'
      },
      style: {
        backgroundColor: 'transparent'
      }
    }
  }
}
